import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";

const Update = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const coverInputRef = useRef(null);

  const userStr = localStorage.getItem("erpUser");
  const userData = userStr ? JSON.parse(userStr) : null;

  const [formData, setFormData] = useState({
    title: "",
    desc: "",
    price: "",
    qty: "",
    cover: "",
    web: "",
    category: "",
    storeid: userData?.userid || "",
    active: "1",
  });

  const [categories, setCategories] = useState([]);
  const [coverPreview, setCoverPreview] = useState("https://placehold.co/300x300?text=Cover+Utama");

  // Existing additional images (fetched from backend)
  const [existingImages, setExistingImages] = useState([]);

  // New additional images to upload
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [newDescs, setNewDescs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // New states for edit/delete images
  const [imageLoading, setImageLoading] = useState({}); // imageId → true/false
  const [editMode, setEditMode] = useState({});         // imageId → true/false
  const [editDesc, setEditDesc] = useState({});         // imageId → temp description

  // Fetch product + existing images + categories
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // 1. Product details
        const bookRes = await axios.get(`https://api.kamulyaan.id/books/${id}`);
        const book = bookRes.data;

        setFormData({
          title: book.title || "",
          desc: book.desc || "",
          price: book.price?.toString() || "",
          qty: book.qty?.toString() || "",
          cover: book.cover || "",
          web: book.web || "",
          category: book.category || "",
          storeid: book.storeid || userData?.userid || "",
          active: book.active?.toString() ?? "1",
        });

        if (book.cover) {
          setCoverPreview(book.cover);
        } else {
          setCoverPreview("https://placehold.co/300x300?text=Cover+Utama");
        }

        // 2. Existing additional images
        const imagesRes = await axios.get(`https://api.kamulyaan.id/addimage/${id}/images`);
        setExistingImages(imagesRes.data.data || imagesRes.data || []);

        // 3. Categories
        const catRes = await axios.get("https://api.kamulyaan.id/category");
        setCategories(catRes.data || []);
      } catch (err) {
        console.error("Load failed:", err);
        setError("Gagal memuat data produk");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setCoverPreview(previewUrl);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 600;
        const scale = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);
        setFormData((prev) => ({ ...prev, cover: compressedBase64 }));
      };
    };
    reader.readAsDataURL(file);
  };

  const handleAdditionalFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const updatedFiles = [...newFiles, ...files].slice(0, 10);
    setNewFiles(updatedFiles);

    const updatedDescs = [...newDescs, ...Array(files.length).fill("")].slice(0, 10);
    setNewDescs(updatedDescs);

    const newPreviewPromises = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;

          img.onload = () => {
            const canvas = document.createElement("canvas");
            const MAX_WIDTH = 600;
            const scale = MAX_WIDTH / img.width;

            canvas.width = MAX_WIDTH;
            canvas.height = img.height * scale;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            resolve(canvas.toDataURL("image/jpeg", 0.7));
          };
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(newPreviewPromises).then((compressedPreviews) => {
      setNewPreviews((prev) => [...prev, ...compressedPreviews].slice(0, 10));
    });
  };

  const handleDescChange = (index, value) => {
    const updated = [...newDescs];
    updated[index] = value;
    setNewDescs(updated);
  };

  // ─── Image Edit / Delete Handlers ──────────────────────────────────────────

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Yakin ingin menghapus gambar ini? Aksi ini tidak bisa dibatalkan.")) return;

    setImageLoading((prev) => ({ ...prev, [imageId]: true }));

    try {
      await axios.delete(`https://api.kamulyaan.id/addimage/${id}/images/${imageId}`);

      // Refresh list
      const imagesRes = await axios.get(`https://api.kamulyaan.id/addimage/${id}/images`);
      setExistingImages(imagesRes.data.data || imagesRes.data || []);

      alert("Gambar berhasil dihapus");
    } catch (err) {
      console.error("Delete image failed:", err);
      alert("Gagal menghapus gambar: " + (err.response?.data?.message || err.message));
    } finally {
      setImageLoading((prev) => ({ ...prev, [imageId]: false }));
    }
  };

  const toggleEdit = (imageId, currentDesc) => {
    setEditMode((prev) => ({ ...prev, [imageId]: !prev[imageId] }));
    if (!editMode[imageId]) {
      setEditDesc((prev) => ({ ...prev, [imageId]: currentDesc || "" }));
    }
  };

  const handleSaveEdit = async (imageId) => {
    const newDesc = (editDesc[imageId] || "").trim();

    setImageLoading((prev) => ({ ...prev, [imageId]: true }));

    try {
      await axios.patch(`https://api.kamulyaan.id/addimage/${id}/images/${imageId}`, {
        desc: newDesc,
      });

      // Refresh list
      const imagesRes = await axios.get(`https://api.kamulyaan.id/addimage/${id}/images`);
      setExistingImages(imagesRes.data.data || imagesRes.data || []);

      // Exit edit mode
      setEditMode((prev) => ({ ...prev, [imageId]: false }));
      setEditDesc((prev) => {
        const updated = { ...prev };
        delete updated[imageId];
        return updated;
      });

      alert("Deskripsi berhasil diperbarui");
    } catch (err) {
      console.error("Update desc failed:", err);
      alert("Gagal memperbarui deskripsi: " + (err.response?.data?.message || err.message));
    } finally {
      setImageLoading((prev) => ({ ...prev, [imageId]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!formData.title.trim() || !formData.price || !formData.category || !formData.storeid) {
      setError("Judul, harga, kategori, dan Store ID wajib diisi!");
      setLoading(false);
      return;
    }

    try {
      // 1. Update main product
      await axios.put(`https://api.kamulyaan.id/books/${id}`, formData);

      // 2. Upload new additional images (if any)
      if (newFiles.length > 0) {
        const formDataImages = new FormData();
        newFiles.forEach((file) => formDataImages.append("images", file));
        newDescs.forEach((desc) => formDataImages.append("desc", desc || ""));

        try {
          await axios.post(
            `https://api.kamulyaan.id/addimage/${id}/images`,
            formDataImages,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        } catch (imgErr) {
          console.warn("Gagal upload gambar tambahan:", imgErr);
          alert("Produk berhasil diperbarui, tetapi beberapa gambar tambahan gagal diupload.");
        }
      }

      alert("Produk berhasil diperbarui!");
      navigate("/");
    } catch (err) {
      console.error("Update failed:", err);
      const errMsg = err.response?.data?.message || err.message || "Gagal memperbarui produk";
      setError(errMsg);
      alert("Gagal memperbarui produk: " + errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-10 text-lg">Memuat data produk...</div>;
  if (error) return <div className="text-red-600 text-center py-10 text-lg">{error}</div>;

  return (
    <div className="app" style={{ padding: "20px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Top Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <Link to="/" style={{ color: "#1976d2", textDecoration: "none", fontWeight: "500" }}>
          ← Kembali ke Daftar
        </Link>
        <h1 style={{ margin: 0, fontSize: "1.8rem" }}>Update Item</h1>
        <div style={{ width: "80px" }} />
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Cover Utama */}
        <div style={{ textAlign: "center" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
            Cover Utama (klik untuk ganti)
          </label>
          <img
            src={coverPreview}
            alt="Cover Preview"
            onClick={() => coverInputRef.current.click()}
            style={{
              width: "240px",
              height: "240px",
              objectFit: "cover",
              cursor: "pointer",
              border: "2px dashed #ccc",
              borderRadius: "12px",
              background: "#f8f9fa",
            }}
          />
          <p style={{ fontSize: "0.9rem", color: "#555", marginTop: "8px" }}>
            Klik gambar untuk mengganti cover
          </p>
          <input
            type="file"
            ref={coverInputRef}
            onChange={handleCoverChange}
            style={{ display: "none" }}
            accept="image/*"
          />
        </div>

        {/* Main fields */}
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Judul produk *"
          required
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        />
        <textarea
          name="desc"
          value={formData.desc}
          onChange={handleChange}
          placeholder="Deskripsi lengkap produk"
          rows={6}
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        />
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={handleChange}
          placeholder="Harga *"
          required
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        />
        <input
          type="number"
          name="qty"
          value={formData.qty}
          onChange={handleChange}
          placeholder="Jumlah stok"
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        />
        <input
          type="text"
          name="web"
          value={formData.web}
          onChange={handleChange}
          placeholder="Link Web / Toko (opsional)"
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        />
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        >
          <option value="">Pilih kategori</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.category}>
              {cat.category}
            </option>
          ))}
        </select>
        <input
          type="text"
          name="storeid"
          value={formData.storeid}
          readOnly
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            background: "#f0f0f0",
            color: "#555",
            cursor: "not-allowed",
          }}
        />
        <select
          name="active"
          value={formData.active}
          onChange={handleChange}
          style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
        >
          <option value="1">Aktif</option>
          <option value="0">Nonaktif</option>
        </select>

        {/* Existing Additional Images */}
        {existingImages.length > 0 && (
          <div style={{ marginTop: "32px" }}>
            <h3>Gambar Tambahan Saat Ini</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "16px" }}>
              {existingImages.map((img) => {
                const isLoading = imageLoading[img.id];
                const isEditing = editMode[img.id];
                const tempDesc = editDesc[img.id] !== undefined ? editDesc[img.id] : img.desc || "";

                return (
                  <div
                    key={img.id}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      padding: "16px",
                      background: "#f9f9f9",
                    }}
                  >
                    <img
                      src={`https://api.kamulyaan.id/${img.imagepath}`}
                      alt={`Preview ${img.id}`}
                      style={{
                        width: "180px",
                        height: "180px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        marginBottom: "12px",
                      }}
                    />

                    {isEditing ? (
                      <input
                        type="text"
                        value={tempDesc}
                        onChange={(e) =>
                          setEditDesc((prev) => ({ ...prev, [img.id]: e.target.value }))
                        }
                        placeholder="Deskripsi baru..."
                        maxLength={255}
                        style={{
                          width: "100%",
                          padding: "10px",
                          borderRadius: "6px",
                          border: "1px solid #999",
                          marginBottom: "10px",
                          fontSize: "0.95rem",
                        }}
                      />
                    ) : (
                      <p style={{ fontSize: "0.9rem", color: "#555", marginBottom: "8px" }}>
                        Deskripsi saat ini: {img.desc || "(kosong)"}
                      </p>
                    )}

                    <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(img.id)}
                            disabled={isLoading}
                            style={{
                              padding: "8px 16px",
                              background: isLoading ? "#aaa" : "#27ae60",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: isLoading ? "not-allowed" : "pointer",
                            }}
                          >
                            {isLoading ? "Menyimpan..." : "Simpan"}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleEdit(img.id, img.desc)}
                            disabled={isLoading}
                            style={{
                              padding: "8px 16px",
                              background: "#e0e0e0",
                              color: "#333",
                              border: "none",
                              borderRadius: "6px",
                            }}
                          >
                            Batal
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleEdit(img.id, img.desc)}
                            disabled={isLoading}
                            style={{
                              padding: "8px 16px",
                              background: "#3498db",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: isLoading ? "not-allowed" : "pointer",
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteImage(img.id)}
                            disabled={isLoading}
                            style={{
                              padding: "8px 16px",
                              background: isLoading ? "#aaa" : "#e74c3c",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: isLoading ? "not-allowed" : "pointer",
                            }}
                          >
                            {isLoading ? "Menghapus..." : "Hapus"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* New Additional Images */}
        <div style={{ marginTop: "32px" }}>
          <h3>Tambah Gambar Tambahan (maks. 10)</h3>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleAdditionalFilesChange}
            style={{ margin: "16px 0", display: "block" }}
          />

          {newFiles.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginTop: "24px" }}>
              {newFiles.map((file, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px dashed #aaa",
                    borderRadius: "12px",
                    padding: "16px",
                    background: "#f9f9f9",
                  }}
                >
                  <img
                    src={newPreviews[idx] || URL.createObjectURL(file)}
                    alt="new preview"
                    style={{
                      width: "180px",
                      height: "180px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginBottom: "16px",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Deskripsi gambar ini (opsional, max 255 karakter)"
                    value={newDescs[idx] ?? ""}
                    onChange={(e) => handleDescChange(idx, e.target.value)}
                    maxLength={255}
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: "40px",
            padding: "16px 32px",
            background: loading ? "#aaa" : "#6bbe23",
            color: "white",
            border: "none",
            borderRadius: "10px",
            fontSize: "1.2rem",
            fontWeight: "bold",
            cursor: loading ? "not-allowed" : "pointer",
            width: "100%",
          }}
        >
          {loading ? "Memperbarui..." : "Update Item"}
        </button>

        {error && (
          <p style={{ color: "red", textAlign: "center", marginTop: "16px" }}>
            {error}
          </p>
        )}

        <Link
          to="/"
          style={{
            marginTop: "24px",
            textAlign: "center",
            display: "block",
            color: "#1976d2",
            textDecoration: "none",
          }}
        >
          ← Batal / Kembali ke Daftar
        </Link>
      </form>
    </div>
  );
};

export default Update;